import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ClassFormDialog from '../components/ClassFormDialog';
import ClassTypeFormDialog from '../components/ClassTypeFormDialog';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Edit,
  Delete,
  DeleteOutline,
  Class,
  Category,
  Add,
  GridView,
  ViewList,
  FileDownloadOutlined,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchChip from '../components/BranchChip';
import CreateClassAtLocationsDialog from '../components/multiLocation/CreateClassAtLocationsDialog';
import ImportClassesDialog from '../components/multiLocation/ImportClassesDialog';
import { useMultiLocationAvailable } from '../components/multiLocation/shared';
import { ALL_LOCATIONS, getSelectedLocation } from '../components/LocationSwitcher';
import { isStudioScopedRole } from '../utils/roles';

interface ClassItem {
  _id: string;
  className: string;
  description: string;
  image?: string;
  duration: number;
  price: number;
  instructor?: {
    _id: string;
    name: string;
    image?: string;
  };
  classType?: {
    title: string;
    studioType?: 'workout' | 'wellness' | 'event';
  };
  // Derived server-side from the chosen ClassType. 'workout' (default) | 'wellness'.
  category?: 'workout' | 'wellness';
  studio: {
    _id: string;
    studioName: string;
  };
  payAtStudio: boolean;
  sessionType?: string;
  createdAt: string;
  updatedAt: string;
}

interface ClassType {
  _id: string;
  title: string;
  image: string;
  studioType: 'event' | 'workout' | 'wellness';
  createdAt?: string;
}

const STUDIO_TYPE_COLORS: Record<ClassType['studioType'], string> = {
  workout: colors.primary,
  wellness: colors.success || colors.info,
  event: colors.warning || colors.secondary,
};

type ViewMode = 'classes' | 'types';

const ClassesPage: React.FC = () => {
  // Which branch each row belongs to, and whether to say so. See
  // useMyLocations: the chip appears only while more than one location is
  // actually being aggregated.
  const { byId: locationById, isAllLocations } = useMyLocations();

  const navigate = useNavigate();
  const location = useLocation();
  const { admin } = useAuth();
  const isSuperAdmin = admin?.role === 'super_admin';

  // Which half of the merged screen is showing. Only super admins get the
  // switch — everyone else only ever had Classes, so they only ever see Classes.
  const [view, setView] = useState<ViewMode>('classes');

  // ----- Classes state -----
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState<ClassItem | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [classToEdit, setClassToEdit] = useState<ClassItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState<ClassItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ----- Class Types state (super admin only) -----
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [typesLoaded, setTypesLoaded] = useState(false);
  const [typeError, setTypeError] = useState<string | null>(null);
  const [typeSearchTerm, setTypeSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<ClassType | null>(null);
  const [typeAnchorEl, setTypeAnchorEl] = useState<null | HTMLElement>(null);
  const [createTypeDialogOpen, setCreateTypeDialogOpen] = useState(false);
  const [editTypeDialogOpen, setEditTypeDialogOpen] = useState(false);
  const [classTypeToEdit, setClassTypeToEdit] = useState<ClassType | null>(null);
  const [deleteTypeDialogOpen, setDeleteTypeDialogOpen] = useState(false);
  const [classTypeToDelete, setClassTypeToDelete] = useState<ClassType | null>(null);
  const [isDeletingType, setIsDeletingType] = useState(false);

  const studioId = (admin as any)?.studio?._id || '';
  const enableSessionTypes = (admin as any)?.studio?.studioPermissions?.enableSessionTypes || false;

  // A brand with two or more locations this account manages creates through
  // the location-aware flow (pick locations, adjust each). Everyone else keeps
  // the single-location dialog unchanged. Super admins reach the same flows
  // from a studio's own page, where the brand is known.
  const isStudioRole = isStudioScopedRole(admin?.role);
  const multiLocation = useMultiLocationAvailable(isStudioRole ? studioId : null) && isStudioRole;
  const viewingLocation = getSelectedLocation();
  const preselect = viewingLocation && viewingLocation !== ALL_LOCATIONS ? [viewingLocation] : [];
  const [multiCreateOpen, setMultiCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    fetchClasses();
    // Auto-open create dialog when navigated here with openCreate state
    if ((location.state as any)?.openCreate) {
      setCreateDialogOpen(true);
      // Clear state so refresh doesn't re-open
      window.history.replaceState({}, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view === 'types' && isSuperAdmin && !typesLoaded) {
      fetchClassTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getAllClasses({});
      console.log('Classes response:', response);
      setClasses(response.classes || []);
    } catch (err: any) {
      console.error('Error fetching classes:', err);
      setError(err.message || 'Failed to fetch classes');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClassTypes = async () => {
    try {
      setIsLoadingTypes(true);
      setTypeError(null);
      const response = await apiService.getClassTypes();
      setClassTypes(response.classTypes || response.data || []);
      setTypesLoaded(true);
    } catch (err: any) {
      setTypeError(err.message || 'Failed to fetch class types');
    } finally {
      setIsLoadingTypes(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, classItem: ClassItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedClass(classItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedClass(null);
  };

  const handleViewClass = () => {
    if (selectedClass) {
      navigate(`/classes/${selectedClass._id}`);
    }
    handleMenuClose();
  };

  const handleEditClass = () => {
    if (selectedClass) {
      setClassToEdit(selectedClass);
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteClass = () => {
    setClassToDelete(selectedClass);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDeleteClass = async () => {
    if (!classToDelete) return;
    try {
      setIsDeleting(true);
      // The row knows where it lives, so the write resolves there even while
      // the switcher says "All locations" (which the server refuses for
      // writes). See apiService.atStudio.
      await apiService.deleteClass(
        classToDelete._id,
        (classToDelete as any)?.studio?._id,
      );
      setClasses(prev => prev.filter(c => c._id !== classToDelete._id));
      setDeleteDialogOpen(false);
      setClassToDelete(null);
    } catch (err: any) {
      console.error('Delete class error:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // ----- Class Type handlers -----
  const handleTypeMenuOpen = (event: React.MouseEvent<HTMLElement>, classType: ClassType) => {
    setTypeAnchorEl(event.currentTarget);
    setSelectedType(classType);
  };

  const handleTypeMenuClose = () => {
    setTypeAnchorEl(null);
    setSelectedType(null);
  };

  const handleEditType = () => {
    if (selectedType) {
      setClassTypeToEdit(selectedType);
      setEditTypeDialogOpen(true);
    }
    handleTypeMenuClose();
  };

  const handleDeleteTypeRequest = () => {
    if (selectedType) {
      setClassTypeToDelete(selectedType);
      setDeleteTypeDialogOpen(true);
    }
    handleTypeMenuClose();
  };

  const confirmDeleteType = async () => {
    if (!classTypeToDelete) return;
    try {
      setIsDeletingType(true);
      await apiService.deleteClassType(classTypeToDelete._id);
      setDeleteTypeDialogOpen(false);
      setClassTypeToDelete(null);
      await fetchClassTypes();
    } catch (err: any) {
      setTypeError(err.response?.data?.message || err.message || 'Failed to delete class type');
      setDeleteTypeDialogOpen(false);
    } finally {
      setIsDeletingType(false);
    }
  };

  const filteredClasses = classes.filter(
    (item) =>
      item.className?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.studio?.studioName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.classType?.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredClassTypes = classTypes.filter(
    (ct) =>
      ct.title?.toLowerCase().includes(typeSearchTerm.toLowerCase()) ||
      ct.studioType?.toLowerCase().includes(typeSearchTerm.toLowerCase())
  );

  const showingClasses = view === 'classes' || !isSuperAdmin;

  return (
    <Box>
      {/* Header */}
      <Box mb={isSuperAdmin ? 2 : 4} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight={700} mb={0.5}>
            {showingClasses ? 'All Classes' : 'Class Types'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {showingClasses
              ? 'Browse and manage classes from all studios'
              : 'Manage the class type catalogue used across all studios'}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {showingClasses && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{ border: `1px solid ${colors.divider}` }}
            >
              <ToggleButton value="grid" sx={{ px: 2 }}>
                <GridView />
              </ToggleButton>
              <ToggleButton value="list" sx={{ px: 2 }}>
                <ViewList />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {showingClasses && multiLocation && (
            <Button variant="outlined" startIcon={<FileDownloadOutlined />} onClick={() => setImportOpen(true)}>
              Import from a location
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() =>
              showingClasses
                ? multiLocation ? setMultiCreateOpen(true) : setCreateDialogOpen(true)
                : setCreateTypeDialogOpen(true)
            }
          >
            {showingClasses ? 'Create Class' : 'Create Class Type'}
          </Button>
        </Box>
      </Box>

      {/* Classes / Class Types switch — same boxed List/Calendar control as Bookings, scaled up */}
      {isSuperAdmin && (
        <Box mb={3}>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, newView) => newView && setView(newView)}
            fullWidth
            sx={{
              bgcolor: colors.backgroundTertiary,
              borderRadius: 2,
              p: 0.75,
              '& .MuiToggleButtonGroup-grouped': {
                border: 0,
                borderRadius: '8px !important',
                mx: 0.5,
                px: 3,
                py: 2,
                textTransform: 'none',
                alignItems: 'flex-start',
                flex: 1,
                '&.Mui-selected': {
                  bgcolor: colors.white,
                  color: colors.textPrimary,
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                  '&:hover': { bgcolor: colors.white },
                  '& svg': { color: colors.primary },
                },
                '&:not(.Mui-selected)': {
                  color: colors.textSecondary,
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                },
              },
            }}
          >
            <ToggleButton value="classes">
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Class sx={{ fontSize: 22 }} />
                  <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                    Classes
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ display: 'block', mt: 0.5, pl: 4, lineHeight: 1.35, opacity: 0.85 }}
                >
                  Browse and manage classes from all studios
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="types">
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Category sx={{ fontSize: 22 }} />
                  <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                    Class Types
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ display: 'block', mt: 0.5, pl: 4, lineHeight: 1.35, opacity: 0.85 }}
                >
                  Catalogue used across all studios
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Both panels stay mounted once visited — toggling `display` instead of
          unmounting keeps already-loaded images in the DOM so switching tabs
          doesn't force them to redecode/reload. */}
      <Box sx={{ display: showingClasses ? 'block' : 'none' }}>
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Search Bar */}
          <Box mb={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by class name, studio, or type..."
              value={searchTerm}
              onChange={handleSearch}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: colors.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : filteredClasses.length === 0 ? (
            <Card>
              <CardContent sx={{ py: 4, textAlign: 'center' }}>
                <Class sx={{ fontSize: 64, color: colors.textSecondary, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" fontWeight={600} mb={1}>
                  No Classes Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ? 'Try adjusting your search terms' : 'Get started by creating a new class'}
                </Typography>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 3 }}>
              {filteredClasses.map((item) => (
                <Card
                  key={item._id}
                  onClick={() => navigate(`/classes/${item._id}`)}
                  sx={{
                    border: `2px solid ${colors.primary}20`,
                    borderRadius: 3,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      boxShadow: `0 8px 24px ${colors.primary}20`,
                      transform: 'translateY(-8px)',
                      borderColor: colors.primary,
                    },
                  }}
                >
                  {item.image && (
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: 180,
                        overflow: 'hidden',
                        backgroundColor: colors.backgroundTertiary,
                      }}
                    >
                      <Box
                        component="img"
                        src={item.image}
                        alt={item.className}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          },
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          borderRadius: 1,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, item);
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                  <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                      <Avatar
                        src={item.instructor?.image}
                        sx={{
                          width: 48,
                          height: 48,
                          backgroundColor: `${colors.secondary}20`,
                          border: `2px solid ${colors.secondary}`,
                        }}
                      >
                        {item.instructor?.name?.[0] || 'C'}
                      </Avatar>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="h6" fontWeight={700} mb={0.5} sx={{
                          fontSize: '1.1rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {item.className}
                        </Typography>
                        {item.classType && (
                          <Chip
                            label={item.classType.title}
                            size="small"
                            sx={{
                              backgroundColor: `${colors.info}15`,
                              color: colors.info,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 24,
                              px: 0.5,
                            }}
                          />
                        )}
                        {item.sessionType === 'private' && (
                          <Chip label="Private" size="small" sx={{ backgroundColor: '#6A1B9A20', color: '#6A1B9A', fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                        )}
                        {item.sessionType === 'duet' && (
                          <Chip label="Duet" size="small" sx={{ backgroundColor: '#00838F20', color: '#00838F', fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                        )}
                        {(item.category === 'wellness' || item.classType?.studioType === 'wellness') && (
                          <Chip label="Wellness" size="small" sx={{ ml: 0.5, backgroundColor: '#00838F20', color: '#00838F', fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                        )}
                      </Box>
                    </Box>

                    <Box sx={{
                      p: 2,
                      backgroundColor: colors.backgroundTertiary,
                      borderRadius: 2,
                      display: 'flex',
                      gap: 2,
                    }}>
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          Duration
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                          {item.duration} min
                        </Typography>
                      </Box>
                      <Box flex={1} textAlign="right">
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          Price
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color={colors.primary}>
                          ₹{item.price.toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Box>
                    {item.studio && (
                      <Box mt={2} display="flex" alignItems="center" gap={1}>
                        {isAllLocations ? (
                          <BranchChip location={locationById(item.studio._id)} />
                        ) : (
                          <>
                            <Typography variant="caption" color="text.secondary">
                              Studio:
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {item.studio.studioName}
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredClasses.map((item) => (
                <Card
                  key={item._id}
                  onClick={() => navigate(`/classes/${item._id}`)}
                  sx={{
                    border: `1px solid ${colors.divider}`,
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      borderColor: colors.secondary,
                    },
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      {item.image && (
                        <Box
                          component="img"
                          src={item.image}
                          alt={item.className}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 2,
                            objectFit: 'cover',
                          }}
                        />
                      )}
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                          <Typography variant="h6" fontWeight={600}>
                            {item.className}
                          </Typography>
                          {item.classType && (
                            <Chip
                              label={item.classType.title}
                              size="small"
                              sx={{
                                backgroundColor: `${colors.secondary}15`,
                                color: colors.secondary,
                                fontWeight: 500,
                              }}
                            />
                          )}
                          {item.sessionType === 'private' && (
                            <Chip label="Private" size="small" sx={{ backgroundColor: '#6A1B9A20', color: '#6A1B9A', fontWeight: 600, fontSize: '0.7rem' }} />
                          )}
                          {item.sessionType === 'duet' && (
                            <Chip label="Duet" size="small" sx={{ backgroundColor: '#00838F20', color: '#00838F', fontWeight: 600, fontSize: '0.7rem' }} />
                          )}
                          {(item.category === 'wellness' || item.classType?.studioType === 'wellness') && (
                            <Chip label="Wellness" size="small" sx={{ backgroundColor: '#00838F20', color: '#00838F', fontWeight: 600, fontSize: '0.7rem' }} />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {item.description}
                        </Typography>
                        <Box display="flex" gap={3}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" color="text.secondary">
                              Duration: {item.duration} mins
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" color="text.secondary">
                              Price: ₹{item.price.toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            {isAllLocations ? (
                              <BranchChip location={locationById(item.studio?._id)} />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Studio: {item.studio?.studioName || 'N/A'}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, item);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Menu */}
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={handleViewClass}>
              <Class sx={{ fontSize: 20, mr: 1 }} /> View Details
            </MenuItem>
            <MenuItem onClick={handleEditClass}>
              <Edit sx={{ fontSize: 20, mr: 1 }} /> Edit
            </MenuItem>
            <MenuItem onClick={handleDeleteClass} sx={{ color: 'error.main' }}>
              <Delete sx={{ fontSize: 20, mr: 1 }} /> Delete
            </MenuItem>
          </Menu>

          {/* Create Class Dialog */}
          {createDialogOpen && (
            <ClassFormDialog
              open={createDialogOpen}
              onClose={() => setCreateDialogOpen(false)}
              onSuccess={() => { setCreateDialogOpen(false); fetchClasses(); }}
              studioId={studioId}
              enableSessionTypes={enableSessionTypes}
            />
          )}

          {/* Create at several locations / import — brands with 2+ locations */}
          {multiLocation && (
            <>
              <CreateClassAtLocationsDialog
                open={multiCreateOpen}
                onClose={() => { setMultiCreateOpen(false); fetchClasses(); }}
                onCreated={() => fetchClasses()}
                anchorStudioId={studioId}
                preselect={preselect}
              />
              <ImportClassesDialog
                open={importOpen}
                onClose={() => { setImportOpen(false); fetchClasses(); }}
                onCreated={() => fetchClasses()}
                anchorStudioId={studioId}
                preselectTarget={preselect[0] ?? null}
              />
            </>
          )}

          {/* Edit Class Dialog */}
          {editDialogOpen && classToEdit && (
            <ClassFormDialog
              open={editDialogOpen}
              onClose={() => { setEditDialogOpen(false); setClassToEdit(null); }}
              onSuccess={() => { setEditDialogOpen(false); setClassToEdit(null); fetchClasses(); }}
              classItem={classToEdit}
              studioId={classToEdit.studio?._id || studioId}
              enableSessionTypes={enableSessionTypes}
            />
          )}

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
            <DialogTitle>Delete Class</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete "{classToDelete?.className}"? This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>Cancel</Button>
              <Button onClick={confirmDeleteClass} color="error" variant="contained" disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      </Box>

      {isSuperAdmin && (view === 'types' || typesLoaded) && (
        <Box sx={{ display: showingClasses ? 'none' : 'block' }}>
        <>
          {typeError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {typeError}
            </Alert>
          )}

          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ py: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by title or studio type..."
                value={typeSearchTerm}
                onChange={(e) => setTypeSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: colors.textSecondary }} />
                    </InputAdornment>
                  ),
                }}
              />
            </CardContent>
          </Card>

          {isLoadingTypes ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : filteredClassTypes.length === 0 ? (
            <Card>
              <CardContent sx={{ py: 4, textAlign: 'center' }}>
                <Category sx={{ fontSize: 64, color: colors.textSecondary, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" fontWeight={600} mb={1}>
                  No Class Types Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {typeSearchTerm ? 'Try adjusting your search terms' : 'Create the first class type to get started'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              {filteredClassTypes.map((classType) => (
                <Card
                  key={classType._id}
                  sx={{
                    border: `1px solid ${colors.divider}`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transform: 'translateY(-4px)',
                      borderColor: colors.secondary,
                    },
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Avatar
                        src={classType.image}
                        variant="rounded"
                        sx={{ width: 72, height: 72, backgroundColor: colors.primary }}
                      >
                        {classType.title?.charAt(0) || 'C'}
                      </Avatar>
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Typography variant="h6" fontWeight={600}>
                            {classType.title}
                          </Typography>
                          <IconButton size="small" onClick={(e) => handleTypeMenuOpen(e, classType)}>
                            <MoreVert />
                          </IconButton>
                        </Box>
                        <Chip
                          label={classType.studioType}
                          size="small"
                          sx={{
                            backgroundColor: `${STUDIO_TYPE_COLORS[classType.studioType] || colors.primary}15`,
                            color: STUDIO_TYPE_COLORS[classType.studioType] || colors.primary,
                            fontSize: '0.7rem',
                            height: 20,
                            textTransform: 'capitalize',
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          <Menu anchorEl={typeAnchorEl} open={Boolean(typeAnchorEl)} onClose={handleTypeMenuClose}>
            <MenuItem onClick={handleEditType}>
              <Edit sx={{ fontSize: 20, mr: 1 }} /> Edit
            </MenuItem>
            <MenuItem onClick={handleDeleteTypeRequest} sx={{ color: 'error.main' }}>
              <DeleteOutline sx={{ fontSize: 20, mr: 1 }} /> Delete
            </MenuItem>
          </Menu>

          {createTypeDialogOpen && (
            <ClassTypeFormDialog
              open={createTypeDialogOpen}
              onClose={() => setCreateTypeDialogOpen(false)}
              onSuccess={() => { setCreateTypeDialogOpen(false); fetchClassTypes(); }}
            />
          )}

          {classTypeToEdit && (
            <ClassTypeFormDialog
              open={editTypeDialogOpen}
              onClose={() => { setEditTypeDialogOpen(false); setClassTypeToEdit(null); }}
              onSuccess={() => { setEditTypeDialogOpen(false); setClassTypeToEdit(null); fetchClassTypes(); }}
              classType={classTypeToEdit}
            />
          )}

          <Dialog open={deleteTypeDialogOpen} onClose={() => setDeleteTypeDialogOpen(false)}>
            <DialogTitle>Delete Class Type</DialogTitle>
            <DialogContent>
              <Typography>
                Delete <strong>{classTypeToDelete?.title}</strong>? Classes already using this type will keep
                a reference to a deleted class type, so make sure nothing active depends on it first.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteTypeDialogOpen(false)} disabled={isDeletingType}>Cancel</Button>
              <Button onClick={confirmDeleteType} color="error" variant="contained" disabled={isDeletingType}>
                {isDeletingType ? <CircularProgress size={20} /> : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
        </Box>
      )}
    </Box>
  );
};

export default ClassesPage;
